<template>
  <div id="home">
    <v-container class="section">
      <div :style="{ cursor: 'pointer' }" @click="$router.push({ name: 'signup' })">
        <v-alert
            class="text-h6 mb-4"
            color="primary"
            dark
            icon="mdi-lightbulb"
            :elevation="4"
        >
          Sign up for a <b><i>free</i></b> account to convert documents, unlock larger conversions, and more!
        </v-alert>
      </div>

      <v-row>
        <v-col cols="12" md="6">
          <v-sheet elevation="4">
            <div
                class="intro-video-wrapper"
                :class="{
                  playing: isPlaying,
                  controlsVisible: showControls
                }"
            >
              <video
                  ref="introVideo"
                  id="introVideo"
                  autoplay
                  muted
                  loop
                  playsinline
                  preload="auto"
                  @click="handleVideoTap"
              >
                <source src="@/assets/video.webm" type="video/webm">
                <source src="@/assets/video.mp4" type="video/mp4">
              </video>
              <button
                  class="play-btn"
                  @click.stop="togglePlay"
                  aria-label="Play Pause"
              >
                <svg v-if="!isPlaying" viewBox="0 0 24 24">
                  <polygon points="5,3 19,12 5,21"/>
                </svg>

                <svg v-else viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16"/>
                  <rect x="14" y="4" width="4" height="16"/>
                </svg>
              </button>

              <button
                  class="sound-btn"
                  @click.stop="toggleMute"
                  aria-label="Mute Toggle"
              >
                <svg v-if="isMuted" viewBox="0 0 24 24">
                  <path d="M4 9v6h4l5 5V4L8 9H4z"/>
                  <line x1="16" y1="8" x2="22" y2="16"/>
                  <line x1="22" y1="8" x2="16" y2="16"/>
                </svg>

                <svg v-else viewBox="0 0 24 24">
                  <path d="M4 9v6h4l5 5V4L8 9H4z"/>
                  <path d="M16 12c0-1.77-1.02-3.29-2.5-4.03v8.06C14.98 15.29 16 13.77 16 12z"/>
                </svg>
              </button>
            </div>
          </v-sheet>
        </v-col>
        <v-col class="d-flex align-center" cols="12" md="6">
          <div>
            <h3 class="text-h3">Support striving readers, those with dyslexia, and multilingual learners</h3>
            <p>Noah Text® is a patented, evidence-based method of displaying text that empowers striving readers through clear, visual word-level scaffolds. By highlighting syllable and long-vowel patterns, making the structure of words visible, Noah Text® supports accurate, independent decoding while reducing cognitive load—allowing readers to focus on comprehension as they build fluency, confidence, and overall reading skill.</p>
            <p>With our StrongReader™ Builder tool, individuals and teachers can convert text pasted from any device, as well as Word documents, into Noah Text®, including:</p>
            <ul class="mb-2">
              <li>Reading passages</li>
              <li>Articles and poetry</li>
              <li>Worksheets</li>
              <li>Tests and quizzes</li>
            </ul>

            <p class="bonus-text"><strong>Bonus!</strong> When you sign up for a free account, you can also access our Chrome browser extension for free. Use the same log-in credentials to convert web pages into Noah Text®. Go to the {{ storeName }} to access the extension <a :href="extensionStoreUrl" target="_blank" rel="noopener" title="StrongReader Addon">here.</a></p>

            <div class="d-flex">
              <v-btn @click="tryItOut" color="secondary" class="mr-2" x-large>Get Started</v-btn>
              <v-btn @click="$refs.soundKeyDialog.open()" x-large>How it works</v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-sheet :color="sectionClass">
      <v-container ref="tryitout" class="section">
        <PageHeader :divider="false" title="Try it out!"></PageHeader>
        <p class="mb-0">Type or copy some text into the field below and click <b>Convert</b> to see it in action.</p>
        <p>To unlock larger conversions, <router-link :to="{ name: 'signup' }">sign up for a <b>free</b> account</router-link>!</p>

        <v-textarea
            class="mb-2"
            v-model="text"
            filled
            clearable
            :counter="maxInput"
            label="Enter text to convert to Noah Text®"
        ></v-textarea>

        <v-row>
          <v-col cols="12" sm="9">
            <v-btn
                block
                color="primary"
                :disabled="!text || !text.trim()"
                :loading="loading"
                @click="convert"
            >
              <v-icon>mdi-format-text-rotation-none</v-icon>
              Convert
            </v-btn>
          </v-col>
          <v-col cols="12" sm="3">
            <v-btn
                block
                color="secondary"
                :disabled="transcript.length === 0"
                @click="copy"
            >
              <v-icon>mdi-content-copy</v-icon>
              <span v-if="copied">Copied!</span>
              <span v-else>Copy</span>
            </v-btn>
          </v-col>
        </v-row>

        <v-card v-if="!loading && transcript.length > 0" class="mt-4">
          <v-card-title v-if="improvement !== null">StrongReader has improved readability by altering {{(improvement * 100).toFixed(0)}}% of the words in this text.</v-card-title>
          <v-divider></v-divider>
          <div class="transcription-syllable transcription" ref="transcription" v-html="html"></div>
        </v-card>
      </v-container>
    </v-sheet>

    <SoundKey ref="soundKeyDialog"></SoundKey>
  </div>
</template>

<script>
import { NoahTextAPI } from '@/common/noahtext-api';
import SoundKey from '@/components/general/SoundKey';
import PageHeader from '@/components/general/PageHeader';

export default {
  name: 'Convert',
  components: {PageHeader, SoundKey},
  data: () => ({
    text: '',
    loading: false,
    transcript: [],
    improvement: null,
    copied: false,
    copiedTimeout: null,
    maxInput: 1000,
    carousel: '0',
    isPlaying: true,
    isMuted: true,
    showControls: false,
    hideTimer: null,
    firefoxStoreUrl: 'https://addons.mozilla.org/en-US/firefox/addon/strongreader/',
    chromeStoreUrl: 'https://chromewebstore.google.com/detail/strongreader/fikpcalcdjahmldcgjmojdeokgoilcok',
    testimonials: [
      {
        content: 'If all books were in Noah Text®, I would read everything. It makes reading so much easier!',
        author: 'High School Student/Striving Reader'
      },
      {
        content: 'I am extremely pleased with how Noah Text® helps the striving reader come into his own as a proficient reader.',
        author: 'Jennifer Smeltser, The Old Schoolhouse Magazine'
      },
      {
        content: 'My students are showing remarkable improvement especially in confidence in decoding unfamiliar words during reading.',
        author: 'Norma Wallace, Teacher, Northern State Prison, NJ'
      }
    ],
    partners: [
      {
        name: 'New Century Education',
        image: 'newcentury.png'
      },
      {
        name: 'Green Kids Club',
        image: 'greenkids.png'
      },
      {
        name: 'Rhode Island Tutorial & Educational Services',
        image: 'rites.png'
      },
      {
        name: 'The Mad Dash',
        image: 'themaddash.png'
      },
      {
        name: 'Correctional Education Association Region One',
        image: 'cearo.png'
      },
      {
        name: 'Educational Advisor',
        image: 'advisor.png'
      },
      {
        name: 'Mackin VIA',
        image: 'mackin.png'
      }
    ]
  }),
  computed: {
    html() {
      return this.transcript.map((t) => `<p>${t.trim() ? t : t + '<br>'}</p>`).join('');
    },
    sectionClass() {
      return this.$vuetify.theme.dark ? 'secondary darken-4' : 'green lighten-5';
    },
    extensionStoreUrl() {
      const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
      return isFirefox ? this.firefoxStoreUrl : this.chromeStoreUrl;
    },
    storeName() {
      return navigator.userAgent.toLowerCase().includes('firefox')
          ? 'Firefox Add-ons Store'
          : 'Chrome Web Store';
    }
  },
  methods: {
    async convert() {
      this.loading = true;
      const response = await NoahTextAPI.convertHTMLFree(this.text.slice(0, this.maxInput).split('\n'));
      this.improvement = response.improvement;
      this.transcript = response.transcript;
      this.loading = false;
    },
    copy() {
      // Clear the cooldown on the previous message
      clearTimeout(this.copied);

      // Manually copy the contents of the transcription
      const range = document.createRange();
      range.selectNode(this.$refs.transcription);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();

      // Display a "copied!" message
      this.copied = true;
      this.copiedTimeout = setTimeout(() => this.copied = false, 2500);
    },
    tryItOut() {
      const section = this.$refs.tryitout;
      const top = section.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: top - 64, behavior: 'smooth' });
    },
    handleVideoTap() {
      // Mobile tap toggles controls only
      if (this.isTouchDevice()) {
        this.toggleControls();
      } else {
        this.togglePlay();
      }
    },
    togglePlay() {
      const video = this.$refs.introVideo;

      if (video.paused) {
        video.play();
        this.isPlaying = true;
      } else {
        video.pause();
        this.isPlaying = false;
      }
    },
    toggleMute() {
      const video = this.$refs.introVideo;

      video.muted = !video.muted;
      this.isMuted = video.muted;
    },
    toggleControls() {
      this.showControls = !this.showControls;
      // Auto-hide after 3s
      if (this.showControls) {
        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => {
          this.showControls = false;
        }, 3000);
      }
    },
    isTouchDevice() {
      return window.matchMedia('(hover: none)').matches;
    }
  },
  mounted() {
    const video = this.$refs.introVideo;

    video.muted = true;
    this.isMuted = true;

    video.play().catch(() => {
      this.isPlaying = false;
    });
  }
}
</script>

<style scoped>

.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.section {
  padding: 32px;
}

.intro-video-wrapper {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  display: flex;
}

.intro-video-wrapper video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  touch-action: manipulation;
}

.intro-video-wrapper .play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: none;

  background: rgba(0,0,0,.6);
  backdrop-filter: blur(6px);

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  opacity: 0;
  transition: .25s ease;
}

.intro-video-wrapper .sound-btn {
  position: absolute;
  bottom: 20px;
  right: 20px;

  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;

  background: rgba(0,0,0,.6);
  backdrop-filter: blur(6px);

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  opacity: 0;
  transition: .25s ease;
}

.intro-video-wrapper:hover .play-btn,
.intro-video-wrapper:hover .sound-btn {
  opacity: 1;
}

.intro-video-wrapper.controlsVisible .play-btn,
.intro-video-wrapper.controlsVisible .sound-btn {
  opacity: 1;
}

.intro-video-wrapper button svg {
  width: 22px;
  height: 22px;
  fill: white;
}

.intro-video-wrapper button svg path {
  fill: white;
}

.intro-video-wrapper button svg line {
  stroke: white;
  stroke-width: 2;
  stroke-linecap: round;
}

.bonus-text {
  color: #0070c0;
}

.bonus-text a {
  color: #ee0000;
}
.bonus-text a:hover {
  color: #ff7624;
}
</style>